import { db } from '../config/firebase';
import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  collection,
  serverTimestamp,
} from 'firebase/firestore';
import { writeAuditLog } from './auditService';

// ─── FX YEAR-END RATE REGISTRY ────────────────────────────────────────────────
//
// Collection: /fx_year_end_rates/{CURRENCY}   (doc id = uppercase ISO 4217)
//
// Holds one YEAR-END USD rate per reporting currency per calendar year. All
// fiscal years in this app end in DECEMBER, so a year's rate is that year's
// December close, keyed by plain calendar year.
//
// ── ORIENTATION — read this before touching any rate ──────────────────────────
// Rates are stored as USD PER 1 UNIT of the reporting currency.
//
//     GHS end-2023  ->  ~0.083     (NOT ~12)
//     USD           ->   1.0
//
// This is the INVERSE of how FX is normally quoted ("the cedi closed at 12 to
// the dollar"), so it is the single easiest thing to get wrong here. The
// orientation is not a preference — it is forced by the consuming math:
// convertMonetaryValue computes `value * rate` with base === the reporting
// currency (functions/index.js), so `value_in_reporting_currency * rate` must
// yield USD. assertPlausibleRate below guards against an inverted entry.
//
// ── SHARED CONTRACT — change both sides together ──────────────────────────────
// These documents are READ server-side by getYearEndRatesForCurrency in
// functions/index.js, using the Admin SDK. That code cannot import this file:
// functions/ is a separate CommonJS package and never imports from src/, and
// this module needs the firebase web SDK. So the document shape is duplicated
// by necessity. If you change the shape here, change it there too.
//
//   /fx_year_end_rates/{CURRENCY}
//     { currency_code, rates: { "2023": { rate, status, ... } } }
//
// This file is the WRITER and owns all validation — the orientation guard and
// the closed-year lock. The engine only reads. Tracked as OI-019.
//
// The consumer is the USD-per-year CAGR formula, which pairs a figure
// question's fiscal_year with the rate for that year.
// ──────────────────────────────────────────────────────────────────────────────

const COLLECTION = 'fx_year_end_rates';

// Hard plausibility band for a USD-per-unit rate.
//
// Upper bound: the strongest real-world currency (KWD) trades near 3.3 USD per
// unit, so 5 leaves headroom without admitting an inverted weak-currency rate
// (an inverted GHS is ~12, NGN ~1500, UGX ~3700 — all far above 5).
// Lower bound: the weakest currencies (IRR, VND, LBP) sit near 1e-5, so 1e-6
// leaves headroom while still rejecting a zero-ish typo.
//
// LIMITATION, stated deliberately: this cannot catch inversion for currencies
// near parity with the dollar. An inverted EUR (1/1.08 = 0.926) falls inside
// the band and passes. The band catches the dangerous, common case — weak
// currencies, where inversion is a 100x-10,000x error — and is NOT a proof of
// correctness. checkAgainstNeighbourYear below is the soft second line.
export const RATE_MIN = 1e-6;
export const RATE_MAX = 5;

// A new rate this many times away from the nearest stored year for the same
// currency is suspicious enough to surface, but NOT to reject: real
// devaluations can be severe (NGN roughly halved across 2023-2024, ~2x). 10x is
// well clear of genuine market moves while still catching a near-parity
// inversion once at least one year is on record.
export const NEIGHBOUR_WARN_FACTOR = 10;

const CURRENCY_RE = /^[A-Z]{3}$/;

/** @param {string} code @return {string} normalised ISO code */
export function normaliseCurrency(code) {
  return String(code || '').trim().toUpperCase();
}

/**
 * The registry is self-authoritative: any valid 3-letter ISO code is accepted.
 * The admin picker offers known codes as suggestions, not as a constraint, so
 * this deliberately does not consult /countries or any hardcoded currency list.
 * @param {string} code
 * @return {string} the normalised code
 */
export function assertValidCurrency(code) {
  const c = normaliseCurrency(code);
  if (!CURRENCY_RE.test(c)) {
    throw new Error(`Invalid currency code "${code}" — expected 3 letters, e.g. GHS`);
  }
  return c;
}

/** @param {number|string} year @return {number} */
export function assertValidYear(year) {
  const y = Number(year);
  if (!Number.isInteger(y) || y < 1900 || y > 2200) {
    throw new Error(`Invalid year "${year}" — expected a 4-digit calendar year, e.g. 2023`);
  }
  return y;
}

/**
 * Hard orientation guard. Throws when a rate falls outside the plausible band,
 * naming inversion as the likely cause so the error is actionable.
 * @param {number|string} rate
 * @param {string} currency
 * @return {number} the parsed rate
 */
export function assertPlausibleRate(rate, currency) {
  const r = Number(rate);
  if (!isFinite(r) || r <= 0) {
    throw new Error(`Rate for ${currency} must be a positive number (got "${rate}")`);
  }
  if (r > RATE_MAX) {
    throw new Error(
      `Rate ${r} for ${currency} is above the plausible maximum of ${RATE_MAX}. ` +
      `Rates are stored as USD per 1 ${currency} — this looks inverted. ` +
      `If ${currency} trades at ${r} to the dollar, enter ${(1 / r).toPrecision(4)} instead.`
    );
  }
  if (r < RATE_MIN) {
    throw new Error(
      `Rate ${r} for ${currency} is below the plausible minimum of ${RATE_MIN}. ` +
      `Rates are stored as USD per 1 ${currency}.`
    );
  }
  return r;
}

/**
 * Soft second inversion check. Advisory only — never rejects. Compares a new
 * rate against the nearest year already stored for the same currency, and
 * returns a warning string when they differ by more than NEIGHBOUR_WARN_FACTOR.
 * Returns null when there is nothing to compare against.
 * @param {number} rate
 * @param {number} year
 * @param {object} existingRates - the doc's rates map
 * @param {string} currency
 * @return {string|null}
 */
export function checkAgainstNeighbourYear(rate, year, existingRates, currency) {
  const years = Object.keys(existingRates || {})
    .map(Number)
    .filter(y => Number.isInteger(y) && y !== year);
  if (!years.length) return null;

  const nearest = years.reduce((best, y) =>
    Math.abs(y - year) < Math.abs(best - year) ? y : best
  );
  const nearestRate = Number(existingRates[String(nearest)]?.rate);
  if (!isFinite(nearestRate) || nearestRate <= 0) return null;

  const factor = rate > nearestRate ? rate / nearestRate : nearestRate / rate;
  if (factor <= NEIGHBOUR_WARN_FACTOR) return null;

  return (
    `Rate ${rate} for ${currency} ${year} differs from ${nearest} (${nearestRate}) ` +
    `by ~${factor.toFixed(1)}x. Check this is USD per 1 ${currency} and not inverted.`
  );
}

// ─── DEPRECIATION ASSUMPTION (per currency) ───────────────────────────────────
//
// A per-currency ANNUAL depreciation rate — the yearly loss of value of the
// reporting currency against USD — used by the FUTURE USD CAGR to project
// year-end rates forward from the most recent closed year. It is CONFIG, not
// code (Decision 1): it lives on the currency document as
// `usd_depreciation_annual`, never as a constant in the scoring engine.
//
// STORED FORM: a decimal FRACTION (0.10 == 10%/yr). The admin UI enters and
// displays it as a PERCENTAGE (enter 10, store 0.10) — a bare "0.1" in a form is
// ambiguous (10%? 0.1%?), and this is the second-most-likely place after
// orientation for a 100x input error. The conversion helpers below own that
// translation so it is pinned by a test rather than buried in the form.
//
// DIRECTION (the inversion trap, stated once): depreciation is a LOSS OF VALUE
// of the reporting currency, so the stored USD-per-unit year-end rate DECLINES.
// The projection (Stage 2) applies it directly to the registry value, no
// inversion: rate(next) = rate * (1 - usd_depreciation_annual).
//
// This file is the WRITER and owns validation; the engine only reads.

// A depreciation at or above 100%/yr is degenerate: it drives the projected
// year-end rate to zero (or negative), which makes the projected USD series
// meaningless. Rejected outright.
export const DEPRECIATION_MAX = 1;

// Above this, a value is accepted only when the admin explicitly confirms it. A
// currency losing half its USD value in a single year is possible but rare; the
// far likelier cause is a percentage typo (90 for 9). This mirrors the
// closed-year lock's "confirm to proceed" gate — a deliberate high rate is
// allowed, a typo is not silently accepted.
export const DEPRECIATION_CONFIRM_ABOVE = 0.5;

/**
 * Convert an admin-entered percentage to the stored decimal fraction.
 * "10" -> 0.10. The UI collects a percentage; the fraction is what the document
 * and the engine use.
 * @param {number|string} percent
 * @return {number} fraction, or NaN when the input is not a number
 */
export function depreciationPercentToFraction(percent) {
  const p = Number(percent);
  return isFinite(p) ? p / 100 : NaN;
}

/**
 * Inverse of depreciationPercentToFraction, for displaying a stored value in the
 * admin form. 0.10 -> 10.
 * @param {number|string} fraction
 * @return {number} percent, or NaN when the input is not a number
 */
export function depreciationFractionToPercent(fraction) {
  const f = Number(fraction);
  return isFinite(f) ? f * 100 : NaN;
}

/**
 * Plausibility guard for a per-currency depreciation FRACTION (not percentage).
 * Rejects negatives and non-numbers, rejects >= 100%/yr outright (degenerate),
 * and refuses a value >= 50%/yr unless confirmHigh is passed. Returns the parsed
 * fraction. Pure — no Firestore access — so it is unit-testable in isolation.
 * @param {number|string} fraction - USD-value loss per year, 0..1
 * @param {string} currency
 * @param {object} [opts]
 * @param {boolean} [opts.confirmHigh] - admin confirmed an unusually high rate
 * @return {number} the parsed fraction
 */
export function assertPlausibleDepreciation(
  fraction, currency, { confirmHigh = false } = {},
) {
  // Guard blank/nullish BEFORE coercion: Number('') and Number(null) are 0, so
  // a blank field would otherwise slip through as a silent 0% — which reads as
  // "no depreciation" and overstates the USD CAGR. Reject it instead.
  const blank = fraction == null ||
    (typeof fraction === 'string' && fraction.trim() === '');
  const d = Number(fraction);
  if (blank || !isFinite(d) || d < 0) {
    throw new Error(
      `Depreciation for ${currency} must be a non-negative number (got "${fraction}")`
    );
  }
  if (d >= DEPRECIATION_MAX) {
    throw new Error(
      `Depreciation ${(d * 100).toFixed(1)}% for ${currency} is at or above 100%/yr. ` +
      `A currency cannot lose all its USD value in one year — enter a rate below 100%.`
    );
  }
  if (d >= DEPRECIATION_CONFIRM_ABOVE && !confirmHigh) {
    throw new Error(
      `Depreciation ${(d * 100).toFixed(1)}% for ${currency} is unusually high ` +
      `(>= 50%/yr). If this is deliberate, confirm it to proceed — a common cause ` +
      `is entering ${(d * 100).toFixed(0)} instead of ${(d * 10).toFixed(0)} (a 10x typo).`
    );
  }
  return d;
}

// ─── READS ────────────────────────────────────────────────────────────────────

export async function getAllFXRates() {
  const snap = await getDocs(collection(db, COLLECTION));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getFXRatesForCurrency(currency) {
  const c = assertValidCurrency(currency);
  const snap = await getDoc(doc(db, COLLECTION, c));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// ─── WRITES ───────────────────────────────────────────────────────────────────

/**
 * Build the history entry + version bump for a change to an existing doc.
 * Mirrors saveFund's config_version / config_history pattern
 * (src/services/firestoreService.js), with one addition: a `reason` field.
 * docs/03 Decision 4 requires that a reason for change is recorded; the
 * existing implementations omit it. This registry records it, and requires it
 * for a closed-year correction.
 */
function buildVersioning(existing, performedBy, reason) {
  const previousSnapshot = { ...existing };
  delete previousSnapshot.config_history;

  return {
    config_version: (existing.config_version || 1) + 1,
    config_history: [
      ...(existing.config_history || []),
      {
        version: existing.config_version || 1,
        snapshot: previousSnapshot,
        changed_at: new Date().toISOString(),
        changed_by: performedBy || null,
        reason: reason || null,
      },
    ],
  };
}

/**
 * Insert or update one year's rate for one currency.
 *
 * Closed-year lock (docs/03 Decision 4): a year whose status is "closed" is
 * never silently overwritten. Correcting one requires an explicit
 * correction:true plus a reason, and the pre-change document is archived into
 * config_history rather than mutated or deleted.
 *
 * @param {object} args
 * @param {string} args.currency
 * @param {number|string} args.year
 * @param {number|string} args.rate - USD per 1 unit of `currency`
 * @param {string} [args.status] - "provisional" (default) or "closed"
 * @param {string} [args.source] - "manual" | "import" | "vendor"
 * @param {string} [args.note]
 * @param {boolean} [args.correction] - required to change a closed year
 * @param {string} [args.reason] - required when correction is true
 * @param {string} args.performedBy
 * @return {Promise<object>} { currency, year, rate, status, warning }
 */
export async function upsertYearRate({
  currency, year, rate, status = 'provisional', source = 'manual',
  note = '', correction = false, reason = '', performedBy,
}) {
  const c = assertValidCurrency(currency);
  const y = assertValidYear(year);
  const r = assertPlausibleRate(rate, c);

  if (status !== 'provisional' && status !== 'closed') {
    throw new Error(`Invalid status "${status}" — expected "provisional" or "closed"`);
  }

  const existing = await getFXRatesForCurrency(c);
  const existingRates = existing?.rates || {};
  const prior = existingRates[String(y)];

  if (prior && prior.status === 'closed') {
    if (!correction) {
      throw new Error(
        `${c} ${y} is closed and cannot be overwritten. ` +
        `Submit it as a correction with a reason to supersede it.`
      );
    }
    if (!String(reason).trim()) {
      throw new Error(`A reason is required to correct the closed year ${c} ${y}`);
    }
  }

  const warning = checkAgainstNeighbourYear(r, y, existingRates, c);

  const yearEntry = {
    rate: r,
    status,
    source,
    note: note || '',
    set_at: new Date().toISOString(),
    set_by: performedBy || null,
  };

  if (existing) {
    const versioning = correction && prior
      ? buildVersioning(existing, performedBy, reason)
      : {};

    await setDoc(
      doc(db, COLLECTION, c),
      {
        ...existing,
        ...versioning,
        rates: { ...existingRates, [String(y)]: yearEntry },
      },
      { merge: true }
    );
  } else {
    await setDoc(doc(db, COLLECTION, c), {
      id: c,
      currency_code: c,
      rates: { [String(y)]: yearEntry },
      config_version: 1,
      config_history: [],
      created_at: serverTimestamp(),
      created_by: performedBy || null,
    });
  }

  await writeAuditLog({
    action: 'fx_year_end_rate_updated',
    entityType: 'system',
    entityId: c,
    performedBy,
    details: {
      currency: c, year: y, rate: r, status, source,
      correction: !!correction,
      ...(correction ? { reason } : {}),
    },
  });

  return { currency: c, year: y, rate: r, status, warning };
}

/**
 * Set (or update) the per-currency annual depreciation assumption.
 *
 * Stored on the currency document as `usd_depreciation_annual`, a decimal
 * fraction. A change to an EXISTING document is a versioned config change
 * (Decision 4): config_version bumps and the prior document is archived into
 * config_history with a reason, via the same buildVersioning machinery the rate
 * rows use. The rates map is carried through untouched, so existing rate rows
 * are never disturbed. An audit entry is written on every change.
 *
 * An idempotent re-save (same value) is a no-op — it neither versions nor audits
 * — mirroring how an in-place provisional rate overwrite does not churn the
 * version.
 *
 * @param {object} args
 * @param {string} args.currency
 * @param {number|string} args.depreciation - decimal FRACTION (0.10 == 10%/yr)
 * @param {boolean} [args.confirmHigh] - required to set a rate >= 50%/yr
 * @param {string} [args.reason] - recorded in history + audit on a change
 * @param {string} args.performedBy
 * @return {Promise<object>} { currency, depreciation, unchanged? }
 */
export async function setCurrencyDepreciation({
  currency, depreciation, confirmHigh = false, reason = '', performedBy,
}) {
  const c = assertValidCurrency(currency);
  const d = assertPlausibleDepreciation(depreciation, c, { confirmHigh });

  const existing = await getFXRatesForCurrency(c);

  // No-op guard: an idempotent re-save must not churn the version or the audit
  // log.
  if (existing && existing.usd_depreciation_annual === d) {
    return { currency: c, depreciation: d, unchanged: true };
  }

  if (existing) {
    // A change to existing config is versioned (Decision 4). Carry the whole
    // document through — rates included — so no rate row is lost.
    const versioning = buildVersioning(existing, performedBy, reason);
    await setDoc(
      doc(db, COLLECTION, c),
      { ...existing, ...versioning, usd_depreciation_annual: d },
      { merge: true }
    );
  } else {
    await setDoc(doc(db, COLLECTION, c), {
      id: c,
      currency_code: c,
      rates: {},
      usd_depreciation_annual: d,
      config_version: 1,
      config_history: [],
      created_at: serverTimestamp(),
      created_by: performedBy || null,
    });
  }

  await writeAuditLog({
    action: 'fx_depreciation_updated',
    entityType: 'system',
    entityId: c,
    performedBy,
    details: {
      currency: c,
      depreciation: d,
      ...(reason ? { reason } : {}),
    },
  });

  return { currency: c, depreciation: d };
}

/**
 * Reusable import path. Applies the orientation guard and the closed-year lock
 * per row and reports on every row rather than failing the whole batch, so a
 * single bad cell never silently drops a load.
 *
 * This is the ONE mechanism behind both the initial multi-year seed and the
 * future vendor feed — fetchVendorYearEndRates hands its rows straight here.
 *
 * Rows are applied SEQUENTIALLY on purpose: rows for the same currency touch
 * the same document, and the neighbouring-year check needs to see rows already
 * accepted in this batch.
 *
 * @param {Array<{currency: string, year: number|string, rate: number|string}>} rows
 * @param {object} [options]
 * @param {string} [options.performedBy]
 * @param {string} [options.defaultStatus] - status applied to rows lacking one
 * @param {string} [options.source]
 * @param {boolean} [options.allowCorrections] - permit superseding closed years
 * @param {string} [options.reason] - required when allowCorrections is true
 * @return {Promise<object>} { accepted, rejected, warnings, summary }
 */
export async function importRates(rows, options = {}) {
  const {
    performedBy, defaultStatus = 'provisional', source = 'import',
    allowCorrections = false, reason = '',
  } = options;

  if (!Array.isArray(rows)) {
    throw new Error('importRates expects an array of { currency, year, rate } rows');
  }

  const accepted = [];
  const rejected = [];
  const warnings = [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    try {
      const result = await upsertYearRate({
        currency: row.currency,
        year: row.year,
        rate: row.rate,
        status: row.status || defaultStatus,
        source,
        note: row.note || '',
        correction: allowCorrections,
        reason,
        performedBy,
      });
      accepted.push({ index: i, ...result });
      if (result.warning) warnings.push({ index: i, warning: result.warning });
    } catch (err) {
      rejected.push({ index: i, row, reason: err.message });
    }
  }

  return {
    accepted,
    rejected,
    warnings,
    summary: {
      total: rows.length,
      accepted: accepted.length,
      rejected: rejected.length,
      warnings: warnings.length,
    },
  };
}

// ─── VENDOR FEED SEAM ─────────────────────────────────────────────────────────

/**
 * Single seam for a future automated year-end rate feed.
 *
 * TODO(fx-vendor): no vendor is chosen yet, so this makes NO network call and
 * is wired to nothing. To implement:
 *   1. call the vendor here for each {currency, year} December close;
 *   2. map the response to rows of { currency, year, rate } where rate is
 *      USD PER 1 UNIT of currency — invert the vendor's quote if it publishes
 *      units-per-USD, which most do (see the ORIENTATION note at the top);
 *   3. hand the rows to importRates(), which already applies the orientation
 *      guard and the closed-year lock. Do not write documents from here.
 * Credentials must come from configuration, never a code constant (Decision 1).
 *
 * @param {object} _args - { currencies: string[], years: number[] }
 * @return {Promise<Array>} rows for importRates
 */
// eslint-disable-next-line no-unused-vars
export async function fetchVendorYearEndRates(_args = {}) {
  throw new Error('Vendor feed not configured');
}
